import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { getApiUrl } from '@/config/api';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷' },
  { code: 'zh-CN', name: 'Chinese (Simplified)', nativeName: '简体中文', flag: '🇨🇳' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '繁體中文', flag: '🇹🇼' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
] as const;

export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map(l => l.code);

/**
 * Convert a camelCase translation key to readable Title Case text.
 * Used as a last-resort fallback when translations can't be loaded from the API.
 * e.g. "createLeague" → "Create League", "sendFeedback" → "Send Feedback"
 */
function keyToReadable(key: string): string {
  // Strip namespace prefix if present (e.g. "nav:createLeague" → "createLeague")
  const lastPart = key.includes(':') ? key.split(':').pop()! : key;
  // Strip dot-separated prefixes (e.g. "musicService.description" → "description")
  const leaf = lastPart.includes('.') ? lastPart.split('.').pop()! : lastPart;
  // Convert camelCase to spaced words with title case
  return leaf
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGE_CODES,
    // Only load essential namespaces on startup. Others are lazy-loaded
    // automatically when components call useTranslation(['league', ...]).
    ns: ['common', 'nav', 'auth'],
    defaultNS: 'common',
    // Allow loading namespaces on demand when components request them
    partialBundledLanguages: true,
    backend: {
      // loadPath is called per-request, so getApiUrl() is evaluated fresh each time
      loadPath: (_lngs: string[], _namespaces: string[]) => {
        const baseUrl = getApiUrl();
        return `${baseUrl}/api/translations/{{lng}}?ns={{ns}}`;
      },
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'yapzap_language',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false, // React already handles XSS
    },
    react: {
      useSuspense: false, // Don't block rendering if translations fail to load
    },
    // When translations can't load (e.g. Capacitor offline, API not deployed),
    // convert the key name to readable text instead of showing raw camelCase
    parseMissingKeyHandler: keyToReadable,
    returnNull: false,
  });

export default i18n;
